import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTenantProtection } from '../../hooks/useTenantProtection';
import toast from 'react-hot-toast';
import {
  Plus,
  Settings,
  RefreshCw,
  Save,
  Edit,
  Eye,
  LayoutGrid,
  GripVertical,
  ArrowUp,
  ArrowDown,
  Layers,
  Lock,
  Users,
  ChevronRight,
  ChevronDown
} from 'lucide-react';
import ComponentLibrary from './components/ComponentLibrary';
import ComponentRenderer from './components/ComponentRenderer';
import PageManagementModal from './components/PageManagementModal';
import dashboardPageService from '../../services/dashboardPageService';

const Dashboard = () => {
  const { user } = useAuth();
  const { getProtectedTenantCode } = useTenantProtection();

  const [layout, setLayout] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('main');
  const [editMode, setEditMode] = useState(false);
  const [showLibrary, setShowLibrary] = useState(false);
  const [saving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showPageManagement, setShowPageManagement] = useState(false);
  const [allPages, setAllPages] = useState([]);
  const [showPageDropdown, setShowPageDropdown] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  // Check if mobile on mount and resize
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    loadUserLayout();
  }, []);

  const loadUserLayout = async () => {
    try {
      setLoading(true);
      console.log('🔍 Loading user layouts...');
      
      const response = await dashboardPageService.getPages();
      console.log('🔍 getPages response:', response);
      
      const layouts = response.data || response || [];
      console.log('🔍 Parsed layouts:', layouts);
      
      // Store all pages for management modal
      setAllPages(layouts);
      
      // Get default layout or first layout
      let userLayout = layouts.find(l => l.IsDefault) || layouts[0];
      console.log('🔍 Selected layout:', userLayout);

      if (!userLayout) {
        console.log('🔍 No layout found, creating default layout...');
        // Create a default layout
        userLayout = await createDefaultLayout();
        if (!userLayout) {
          console.error('❌ Failed to create default layout');
          toast.error('Failed to create dashboard layout. Please try refreshing the page.');
          return;
        }
      } else {
        console.log('🔍 Loading layout details for:', userLayout.LayoutID);
        // Load full layout with instances
        await loadLayoutDetails(userLayout.LayoutID);
      }
    } catch (error) {
      console.error('❌ Error loading user layout:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data
      });
      toast.error(`Failed to load dashboard layout: ${error.message || 'Unknown error'}`);
      
      // Try to create a default layout as fallback
      try {
        console.log('🔍 Attempting to create fallback default layout...');
        const fallbackLayout = await createDefaultLayout();
        if (fallbackLayout) {
          console.log('✅ Fallback layout created successfully');
        }
      } catch (fallbackError) {
        console.error('❌ Failed to create fallback layout:', fallbackError);
      }
    } finally {
      setLoading(false);
    }
  };

  const createDefaultLayout = async () => {
    try {
      console.log('🔍 Creating default layout...');
      const result = await dashboardPageService.createPage({
        layoutName: 'My Dashboard',
        isDefault: true,
        tabsConfig: [{ id: 'main', label: 'Main', order: 0 }]
      });

      console.log('🔍 Create page result:', result);
      
      // Handle different response formats
      const layoutId = result.data?.id || result.data?.LayoutID || result.id || result?.id;
      if (!layoutId) {
        console.error('❌ No layout ID returned from createPage');
        console.error('❌ Full result:', JSON.stringify(result, null, 2));
        throw new Error('Failed to create layout: No ID returned');
      }

      console.log('🔍 Loading layout details for new layout:', layoutId);
      await loadLayoutDetails(layoutId);
      
      // Verify layout was loaded
      if (!layout) {
        console.warn('⚠️ Layout state not set after loadLayoutDetails, retrying...');
        // Wait a bit and retry
        await new Promise(resolve => setTimeout(resolve, 500));
        await loadLayoutDetails(layoutId);
      }
      
      // Return the layout data that was just loaded (from loadLayoutDetails)
      // The layout state should now be set
      console.log('✅ Default layout created and loaded successfully');
      return { LayoutID: layoutId, LayoutName: 'My Dashboard' };
    } catch (error) {
      console.error('❌ Error creating default layout:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response?.data,
        fullError: error
      });
      toast.error(`Failed to create dashboard layout: ${error.message || 'Unknown error'}`);
      return null;
    }
  };

  const loadLayoutDetails = async (layoutId) => {
    try {
      if (!layoutId) {
        console.error('❌ No layout ID provided to loadLayoutDetails');
        throw new Error('Layout ID is required');
      }

      console.log('🔍 Loading layout details for ID:', layoutId);
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      if (!tenantCode) {
        console.error('❌ No tenant code available');
        throw new Error('Tenant code is required');
      }

      const response = await fetch(`/api/user-layouts/${layoutId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ Failed to load layout details:', response.status, errorText);
        throw new Error(`Failed to load layout details: ${response.statusText}`);
      }

      const data = await response.json();
      console.log('🔍 Layout details loaded:', data);
      
      const layoutData = data.data || data;
      if (!layoutData) {
        throw new Error('No layout data returned');
      }

      setLayout(layoutData);
      console.log('✅ Layout state set:', layoutData);

      // Set active tab to first tab if available
      if (layoutData.tabsConfig && layoutData.tabsConfig.length > 0) {
        setActiveTab(layoutData.tabsConfig[0].id);
        console.log('✅ Active tab set to:', layoutData.tabsConfig[0].id);
      } else {
        console.warn('⚠️ No tabs found in layout');
      }
    } catch (error) {
      console.error('❌ Error loading layout details:', error);
      console.error('❌ Error details:', {
        message: error.message,
        stack: error.stack
      });
      toast.error(`Failed to load layout details: ${error.message || 'Unknown error'}`);
      // Don't set layout to null - keep previous state if available
    }
  };

  // Helper function to find next available grid position
  const findNextGridPosition = (gridWidth, gridHeight, existingInstances) => {
    const GRID_COLUMNS = 4;
    const occupied = new Set();
    
    // Mark all occupied grid cells
    existingInstances.forEach(instance => {
      const col = instance.PositionCol || 0;
      const row = instance.PositionRow || 0;
      const width = instance.Width || 1;
      const height = instance.Height || 1;
      
      for (let r = row; r < row + height; r++) {
        for (let c = col; c < col + width; c++) {
          occupied.add(`${r},${c}`);
        }
      }
    });
    
    // Find first available position
    for (let row = 0; row < 100; row++) { // Max 100 rows
      for (let col = 0; col <= GRID_COLUMNS - gridWidth; col++) {
        // Check if this position can fit the component
        let canFit = true;
        for (let r = row; r < row + gridHeight && canFit; r++) {
          for (let c = col; c < col + gridWidth && canFit; c++) {
            if (occupied.has(`${r},${c}`)) {
              canFit = false;
            }
          }
        }
        
        if (canFit) {
          return { row, col };
        }
      }
    }
    
    // Fallback: place at the end
    const maxRow = existingInstances.length > 0 
      ? Math.max(...existingInstances.map(i => (i.PositionRow || 0) + (i.Height || 1)))
      : 0;
    return { row: maxRow, col: 0 };
  };

  const handleAddComponent = async (component) => {
    if (!layout) {
      console.error('❌ Cannot add component: No layout loaded');
      console.log('🔍 Attempting to create default layout...');
      toast.loading('Creating dashboard layout...');
      
      const newLayout = await createDefaultLayout();
      if (!newLayout) {
        toast.dismiss();
        toast.error('Failed to create dashboard layout. Please refresh the page or contact support.');
        return;
      }
      
      toast.dismiss();
      toast.success('Dashboard layout created! Adding component...');
      
      // Wait a moment for state to update, then reload layout
      await new Promise(resolve => setTimeout(resolve, 500));
      await loadUserLayout();
      
      // Check again if layout is now available
      if (!layout) {
        toast.error('Layout created but not loaded. Please refresh the page.');
        return;
      }
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      // Get grid size from component config, or use defaults
      let gridWidth = 1;
      let gridHeight = 1;
      
      if (component.config && component.config.gridSize) {
        gridWidth = component.config.gridSize.width || 1;
        gridHeight = component.config.gridSize.height || 1;
      } else {
        // Fallback to defaults based on component type
        const defaultSizes = {
          'StatCard': { width: 1, height: 1 },
          'Table': { width: 4, height: 2 },
          'BarChart': { width: 2, height: 2 },
          'LineChart': { width: 2, height: 2 },
          'PieChart': { width: 2, height: 2 }
        };
        const defaultSize = defaultSizes[component.ComponentType] || { width: 1, height: 1 };
        gridWidth = defaultSize.width;
        gridHeight = defaultSize.height;
      }

      // Ensure grid size doesn't exceed 4 columns
      gridWidth = Math.min(gridWidth, 4);
      
      // Find next available position
      const currentInstances = (layout?.instances || []).filter(i => i.TabID === activeTab);
      const position = findNextGridPosition(gridWidth, gridHeight, currentInstances);

      const response = await fetch(`/api/user-layouts/${layout.LayoutID}/add-component`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          componentId: component.ComponentID,
          tabId: activeTab,
          position: {
            row: position.row,
            col: position.col,
            width: gridWidth,
            height: gridHeight
          }
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add component');
      }

      toast.success('Component added to dashboard');
      setShowLibrary(false);
      
      // Reload layout
      await loadLayoutDetails(layout.LayoutID);
    } catch (error) {
      console.error('Error adding component:', error);
      toast.error('Failed to add component');
    }
  };

  const handleRemoveComponent = async (instanceId) => {
    if (!window.confirm('Remove this component from your dashboard?')) {
      return;
    }

    try {
      const tenantCode = getProtectedTenantCode();
      const token = localStorage.getItem('token');

      const response = await fetch(`/api/user-layouts/instances/${instanceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'X-Tenant-Code': tenantCode
        }
      });

      if (!response.ok) {
        throw new Error('Failed to remove component');
      }

      toast.success('Component removed');
      
      // Reload layout
      await loadLayoutDetails(layout.LayoutID);
    } catch (error) {
      console.error('Error removing component:', error);
      toast.error('Failed to remove component');
    }
  };

  const handleRefreshAll = () => {
    // This will trigger a re-render which will cause all components to reload
    setLayout({ ...layout });
    toast.success('Refreshing all components');
  };

  const handlePageCreated = async () => {
    // Reload pages after creation
    await loadUserLayout();
  };

  const handlePageUpdated = async () => {
    // Reload pages after update
    await loadUserLayout();
  };

  const handlePageDeleted = async (deletedPageId) => {
    // If the deleted page was the current page, switch to another page
    if (layout?.LayoutID === deletedPageId) {
      await loadUserLayout();
    } else {
      // Just reload the pages list
      const response = await dashboardPageService.getPages();
      setAllPages(response.data || []);
    }
  };

  const handleSwitchPage = async (pageId) => {
    // Don't reload if already on this page
    if (layout?.LayoutID === pageId) {
      return;
    }

    try {
      setLoading(true);
      await loadLayoutDetails(pageId);
      setShowPageManagement(false);
      toast.success('Page loaded');
    } catch (error) {
      console.error('Error switching page:', error);
      toast.error('Failed to load page');
    } finally {
      setLoading(false);
    }
  };

  // Drag and Drop handlers for reordering
  const handleDragStart = (e, index) => {
    if (!editMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, index) => {
    if (!editMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverIndex(index);
  };

  const handleDragLeave = () => {
    setDragOverIndex(null);
  };

  const handleDrop = async (e, dropIndex) => {
    if (!editMode) return;
    e.preventDefault();
    
    if (draggedIndex === null || draggedIndex === dropIndex) {
      setDraggedIndex(null);
      setDragOverIndex(null);
      return;
    }

    try {
      const currentInstances = [...instances];
      const [draggedInstance] = currentInstances.splice(draggedIndex, 1);
      currentInstances.splice(dropIndex, 0, draggedInstance);

      console.log('🔄 Reordering components:', {
        from: draggedIndex,
        to: dropIndex,
        instanceIds: currentInstances.map(i => i.InstanceID)
      });

      // Update PositionRow for each instance to match new order
      const updatedInstances = currentInstances.map((instance, index) => ({
        ...instance,
        PositionRow: index
      }));

      // Save order to backend FIRST
      await saveComponentOrder(updatedInstances.map(i => i.InstanceID));

      // Update local state after successful save with updated PositionRow values
      const updatedLayout = { ...layout };
      const otherTabInstances = updatedLayout.instances.filter(i => i.TabID !== activeTab);
      updatedLayout.instances = [...otherTabInstances, ...updatedInstances];
      setLayout(updatedLayout);

      toast.success('Component order updated');
    } catch (error) {
      console.error('❌ Error reordering components:', error);
      toast.error('Failed to update component order: ' + error.message);
      // Reload on error
      await loadLayoutDetails(layout.LayoutID);
    }

    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  };

  const handleMoveComponent = async (index, direction) => {
    const currentInstances = [...instances];
    const newIndex = index + direction;

    if (newIndex < 0 || newIndex >= currentInstances.length) {
      return;
    }

    const [movedInstance] = currentInstances.splice(index, 1);
    currentInstances.splice(newIndex, 0, movedInstance);

    try {
      // Update PositionRow for each instance to match new order
      const updatedInstances = currentInstances.map((instance, idx) => ({
        ...instance,
        PositionRow: idx
      }));

      // Save order to backend
      await saveComponentOrder(updatedInstances.map(i => i.InstanceID));

      // Update local state after successful save with updated PositionRow values
      const updatedLayout = { ...layout };
      updatedLayout.instances = [
        ...updatedLayout.instances.filter(i => i.TabID !== activeTab),
        ...updatedInstances
      ];
      setLayout(updatedLayout);

      toast.success('Component moved');
    } catch (error) {
      console.error('Error reordering components:', error);
      toast.error('Failed to update component order');
      // Reload on error
      await loadLayoutDetails(layout.LayoutID);
    }
  };

  const saveComponentOrder = async (orderedInstanceIds) => {
    const tenantCode = getProtectedTenantCode();
    const token = localStorage.getItem('token');

    console.log('💾 Saving component order:', {
      layoutId: layout.LayoutID,
      tabId: activeTab,
      instanceIds: orderedInstanceIds
    });

    const response = await fetch(`/api/user-layouts/${layout.LayoutID}/reorder-components`, {
      method: 'PUT',
      headers: {
        'Authorization': `Bearer ${token}`,
        'X-Tenant-Code': tenantCode,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        tabId: activeTab,
        instanceIds: orderedInstanceIds
      })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('❌ Failed to save component order:', response.status, errorText);
      throw new Error(`Failed to save component order: ${response.statusText}`);
    }

    const result = await response.json();
    console.log('✅ Component order saved successfully:', result);
    return result;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="flex flex-col items-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const tabs = layout?.tabsConfig || [{ id: 'main', label: 'Main' }];
  
  // First, get and sort instances
  let instances = (layout?.instances || [])
    .filter(i => i.TabID === activeTab)
    .sort((a, b) => {
      // Sort by row first, then by column
      const rowDiff = (a.PositionRow || 0) - (b.PositionRow || 0);
      if (rowDiff !== 0) return rowDiff;
      return (a.PositionCol || 0) - (b.PositionCol || 0);
    });
  
  // Auto-fix positioning: redistribute components to fill rows horizontally (left to right, top to bottom)
  const fixedInstances = [];
  const occupied = new Set(); // Track occupied cells as "row,col"
  
  instances.forEach(instance => {
    const width = instance.Width || 1;
    const height = instance.Height || 1;
    
    // Find first available position starting from top-left
    let placed = false;
    for (let row = 0; row < 100 && !placed; row++) {
      for (let col = 0; col <= 4 - width && !placed; col++) {
        // Check if this position can fit the component
        let canFit = true;
        for (let r = row; r < row + height && canFit; r++) {
          for (let c = col; c < col + width && canFit; c++) {
            if (occupied.has(`${r},${c}`)) {
              canFit = false;
            }
          }
        }
        
        if (canFit) {
          // Mark all cells as occupied
          for (let r = row; r < row + height; r++) {
            for (let c = col; c < col + width; c++) {
              occupied.add(`${r},${c}`);
            }
          }
          
          // Add instance with new position
          fixedInstances.push({
            ...instance,
            PositionCol: col,
            PositionRow: row
          });
          
          placed = true;
        }
      }
    }
  });
  
  instances = fixedInstances;

  // Categorize pages for navigation
  const { personal, shared } = dashboardPageService.categorizPages(
    allPages, 
    user?.tenantUserId || user?.userId
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-600 text-white">
        <div className="w-full px-4 sm:px-6 py-4 sm:py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2 sm:space-x-3 flex-1 min-w-0">
              <div className="flex items-center justify-center w-10 h-10 sm:w-12 sm:h-12 bg-white bg-opacity-20 rounded-lg flex-shrink-0">
                <LayoutGrid className="h-5 w-5 sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0 flex-1">
                <h1 className="text-xl sm:text-2xl md:text-3xl font-bold truncate">
                  {layout?.LayoutName || 'Dashboard'}
                </h1>
                <p className="text-teal-100 mt-1 text-sm sm:text-base truncate">
                  Welcome back, {user?.fullName || user?.username}
                  {layout?.IsShared && (
                    <span className="ml-2 px-2 py-0.5 bg-white bg-opacity-20 text-white text-xs font-medium rounded">
                      Shared Page
                    </span>
                  )}
                </p>
              </div>
            </div>
            <div className="hidden md:flex items-center space-x-4 flex-shrink-0">
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <Settings className="h-4 w-4" />
                <span className="text-sm">
                  {editMode ? 'Edit Mode' : 'View Mode'}
                </span>
              </div>
              <div className="flex items-center space-x-2 bg-white bg-opacity-20 px-3 py-2 rounded-lg">
                <LayoutGrid className="h-4 w-4" />
                <span className="text-sm">
                  {instances.length} Components
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Page Navigation Bar - Below Header */}
      {allPages.length > 0 && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 shadow-sm">
          <div className="w-full px-4 sm:px-6 py-3">
            <div className="flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
              {/* Mobile: Dropdown for Page Selection */}
              <div className="md:hidden relative w-full">
                <button
                  onClick={() => setShowPageDropdown(!showPageDropdown)}
                  className="w-full flex items-center justify-between px-4 py-2.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg font-medium transition-colors hover:bg-gray-200 dark:hover:bg-gray-600"
                >
                  <div className="flex items-center gap-2">
                    {layout?.IsShared ? (
                      <Users className="h-4 w-4" />
                    ) : (
                      <Lock className="h-4 w-4" />
                    )}
                    <span className="truncate">{layout?.LayoutName || 'Select Page'}</span>
                    {layout?.IsDefault && (
                      <span className="px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">
                        Default
                      </span>
                    )}
                  </div>
                  <ChevronDown className={`h-4 w-4 transition-transform ${showPageDropdown ? 'rotate-180' : ''}`} />
                </button>
                
                {showPageDropdown && (
                  <>
                    <div 
                      className="fixed inset-0 z-10" 
                      onClick={() => setShowPageDropdown(false)}
                    />
                    <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 max-h-96 overflow-y-auto">
                      {/* Personal Pages Section */}
                      {personal.length > 0 && (
                        <div className="p-2">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Personal Pages
                          </div>
                          {personal.map((page) => (
                            <button
                              key={page.LayoutID}
                              onClick={() => {
                                handleSwitchPage(page.LayoutID);
                                setShowPageDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-all text-left ${
                                layout?.LayoutID === page.LayoutID
                                  ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md'
                                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300'
                              }`}
                            >
                              <Lock className="h-4 w-4 flex-shrink-0" />
                              <span className="flex-1 truncate">{page.LayoutName}</span>
                              {page.IsDefault && (
                                <span className="px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded flex-shrink-0">
                                  Default
                                </span>
                              )}
                            </button>
                          ))}
                        </div>
                      )}

                      {/* Shared Pages Section */}
                      {shared.length > 0 && (
                        <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                            Shared Pages
                          </div>
                          {shared.map((page) => (
                            <button
                              key={page.LayoutID}
                              onClick={() => {
                                handleSwitchPage(page.LayoutID);
                                setShowPageDropdown(false);
                              }}
                              className={`w-full flex items-center gap-2 px-3 py-2.5 rounded-lg font-medium transition-all text-left ${
                                layout?.LayoutID === page.LayoutID
                                  ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md'
                                  : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover:text-cyan-700 dark:hover:text-cyan-300'
                              }`}
                            >
                              <Users className="h-4 w-4 flex-shrink-0" />
                              <span className="flex-1 truncate">{page.LayoutName}</span>
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>

              {/* Desktop: Page Tabs */}
              <div className="hidden md:flex items-center gap-2 overflow-x-auto flex-1">
                {/* Personal Pages */}
                {personal.map((page) => (
                  <button
                    key={page.LayoutID}
                    onClick={() => handleSwitchPage(page.LayoutID)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                      layout?.LayoutID === page.LayoutID
                        ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-teal-50 dark:hover:bg-teal-900/20 hover:text-teal-700 dark:hover:text-teal-300'
                    }`}
                  >
                    <Lock className="h-4 w-4" />
                    <span>{page.LayoutName}</span>
                    {page.IsDefault && (
                      <span className="ml-1 px-1.5 py-0.5 bg-yellow-400 text-yellow-900 text-xs font-bold rounded">
                        Default
                      </span>
                    )}
                  </button>
                ))}

                {/* Shared Pages */}
                {shared.map((page) => (
                  <button
                    key={page.LayoutID}
                    onClick={() => handleSwitchPage(page.LayoutID)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-all whitespace-nowrap ${
                      layout?.LayoutID === page.LayoutID
                        ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white shadow-md'
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-cyan-50 dark:hover:bg-cyan-900/20 hover:text-cyan-700 dark:hover:text-cyan-300'
                    }`}
                  >
                    <Users className="h-4 w-4" />
                    <span>{page.LayoutName}</span>
                  </button>
                ))}
              </div>

              {/* Action Buttons */}
              <div className="flex items-center gap-2 flex-shrink-0">
                <button
                  onClick={() => setShowPageManagement(true)}
                  className="flex items-center gap-2 px-3 py-2 bg-teal-600 hover:bg-teal-700 text-white rounded-lg font-medium transition-colors whitespace-nowrap"
                  title="Manage pages"
                >
                  <Layers className="h-4 w-4" />
                  <span className="hidden sm:inline">Manage</span>
                </button>
                <button
                  onClick={handleRefreshAll}
                  className="flex items-center gap-2 px-3 py-2 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
                  title="Refresh all components"
                >
                  <RefreshCw className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setEditMode(!editMode)}
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg font-medium transition-colors ${
                    editMode 
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white' 
                      : 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
                  }`}
                >
                  {editMode ? <Eye className="w-4 h-4" /> : <Edit className="w-4 h-4" />}
                  <span className="hidden sm:inline">{editMode ? 'View' : 'Edit'}</span>
                </button>
                {editMode && (
                  <button
                    onClick={async () => {
                      if (!layout) {
                        console.log('🔍 No layout found, attempting to create default layout...');
                        toast.loading('Creating dashboard layout...');
                        const newLayout = await createDefaultLayout();
                        if (newLayout) {
                          toast.dismiss();
                          toast.success('Dashboard layout created! You can now add components.');
                          setShowLibrary(true);
                        } else {
                          toast.dismiss();
                          toast.error('Failed to create dashboard layout. Please refresh the page.');
                        }
                      } else {
                        setShowLibrary(true);
                      }
                    }}
                    className="flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-teal-600 to-cyan-600 text-white rounded-lg hover:from-teal-700 hover:to-cyan-700 transition-all"
                  >
                    <Plus className="w-4 h-4" />
                    <span className="hidden sm:inline">Add</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Component Tabs - if multiple tabs in current page */}
      {tabs.length > 1 && (
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="w-full px-4 sm:px-6 py-2">
            <div className="flex items-center gap-2 overflow-x-auto">
              {tabs.map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`px-3 sm:px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                    activeTab === tab.id
                      ? 'bg-gradient-to-r from-teal-600 to-cyan-600 text-white'
                      : 'text-gray-600 dark:text-gray-400 hover:text-teal-600 dark:hover:text-teal-400 hover:bg-teal-50 dark:hover:bg-teal-900/20'
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="w-full px-4 sm:px-6 py-4 sm:py-8">
        {!layout ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sm:p-12 text-center">
            <LayoutGrid className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No Dashboard Layout Found
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
              A dashboard layout is required to add components. Click below to create your dashboard.
            </p>
            <button
              onClick={async () => {
                console.log('🔍 Creating default layout...');
                toast.loading('Creating dashboard layout...');
                const newLayout = await createDefaultLayout();
                if (newLayout) {
                  toast.dismiss();
                  toast.success('Dashboard layout created successfully!');
                  // Layout will be loaded automatically via loadUserLayout
                } else {
                  toast.dismiss();
                  toast.error('Failed to create dashboard layout. Please check the console for details.');
                }
              }}
              className="btn btn-primary flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              Create Dashboard
            </button>
          </div>
        ) : instances.length === 0 ? (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 sm:p-12 text-center">
            <LayoutGrid className="w-12 h-12 sm:w-16 sm:h-16 text-gray-300 dark:text-gray-600 mx-auto mb-4" />
            <h3 className="text-base sm:text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
              No components yet
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-6">
              Add visualization components to your dashboard to get started
            </p>
            <button
              onClick={async () => {
                if (!layout) {
                  console.log('🔍 No layout found, attempting to create default layout...');
                  toast.loading('Creating dashboard layout...');
                  const newLayout = await createDefaultLayout();
                  if (newLayout) {
                    toast.dismiss();
                    toast.success('Dashboard layout created! You can now add components.');
                    setShowLibrary(true);
                  } else {
                    toast.dismiss();
                    toast.error('Failed to create dashboard layout. Please refresh the page.');
                  }
                } else {
                  setShowLibrary(true);
                }
              }}
              className="btn btn-primary flex items-center gap-2 mx-auto"
            >
              <Plus className="w-4 h-4" />
              Add Component
            </button>
          </div>
        ) : (
          <div 
            className="grid grid-cols-1 md:grid-cols-4 gap-4"
            style={{
              gridAutoRows: 'minmax(120px, auto)',
              width: '100%'
            }}
          >
            {instances.map((instance, index) => {
              const gridCol = instance.PositionCol || 0;
              const gridRow = instance.PositionRow || 0;
              const gridWidth = instance.Width || 1;
              const gridHeight = instance.Height || 1;
              
              return (
                <div
                  key={instance.InstanceID}
                  draggable={editMode && !isMobile}
                  onDragStart={(e) => !isMobile && handleDragStart(e, index)}
                  onDragOver={(e) => !isMobile && handleDragOver(e, index)}
                  onDragLeave={!isMobile ? handleDragLeave : undefined}
                  onDrop={(e) => !isMobile && handleDrop(e, index)}
                  onDragEnd={!isMobile ? handleDragEnd : undefined}
                  className={`relative group transition-all ${
                    editMode && draggedIndex === index
                      ? 'opacity-50 scale-95'
                      : editMode && dragOverIndex === index
                      ? 'ring-2 ring-purple-500 scale-105'
                      : ''
                  } ${editMode && !isMobile ? 'cursor-move' : ''}`}
                  style={isMobile ? {
                    gridColumn: 'span 1',
                    gridRow: `span ${gridHeight}`
                  } : {
                    gridColumn: `${gridCol + 1} / span ${gridWidth}`,
                    gridRow: `${gridRow + 1} / span ${gridHeight}`
                  }}
                >
                {editMode && (
                  <>
                    {/* Drag Handle - Hidden on mobile */}
                    <div className="hidden md:block absolute -top-3 left-1/2 transform -translate-x-1/2 z-10 bg-purple-600 text-white rounded px-2 py-1 flex items-center gap-1 shadow-lg opacity-0 group-hover:opacity-100 transition-opacity cursor-grab active:cursor-grabbing">
                      <GripVertical className="w-4 h-4" />
                      <span className="text-xs font-medium">Drag to reorder</span>
                    </div>

                    {/* Reorder Buttons - Hidden on mobile */}
                    <div className="hidden md:flex absolute -top-2 -left-2 z-10 flex-col gap-1">
                      <button
                        onClick={() => handleMoveComponent(index, -1)}
                        disabled={index === 0}
                        className="p-1 bg-purple-500 hover:bg-purple-600 text-white rounded shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move left/up"
                      >
                        <ArrowUp className="w-3 h-3" />
                      </button>
                      <button
                        onClick={() => handleMoveComponent(index, 1)}
                        disabled={index === instances.length - 1}
                        className="p-1 bg-purple-500 hover:bg-purple-600 text-white rounded shadow-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        title="Move right/down"
                      >
                        <ArrowDown className="w-3 h-3" />
                      </button>
                    </div>

                    {/* Remove Button */}
                    <button
                      onClick={() => handleRemoveComponent(instance.InstanceID)}
                      className="absolute -top-2 -right-2 z-10 p-1.5 sm:p-1 bg-red-500 hover:bg-red-600 text-white rounded-full shadow-lg transition-colors"
                      title="Remove component"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </>
                )}
                <ComponentRenderer
                  component={{
                    ComponentID: instance.ComponentID,
                    ComponentType: instance.ComponentType,
                    Name: instance.ComponentName,
                    Description: instance.ComponentDescription,
                    EntityName: instance.EntityName,
                    ConfigJSON: instance.ComponentConfig
                  }}
                  customization={instance.customization}
                />
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Component Library Modal */}
      <ComponentLibrary
        isOpen={showLibrary}
        onClose={() => setShowLibrary(false)}
        onAddComponent={handleAddComponent}
      />

      {/* Page Management Modal */}
      <PageManagementModal
        isOpen={showPageManagement}
        onClose={() => setShowPageManagement(false)}
        pages={allPages}
        currentPage={layout}
        onPageCreated={handlePageCreated}
        onPageUpdated={handlePageUpdated}
        onPageDeleted={handlePageDeleted}
        user={user}
      />
    </div>
  );
};

export default Dashboard;

